import type { JsonSchemaType } from '@gmn-portal/backend';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from '@/core/i18n/use-intl';
import { MetadataAutoForm } from '@/shared/components/metadata/metadata-auto-form';
import {
  Button,
  Form,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui';
import { cn, scrollToTarget } from '@/shared/utils';
import { createMetadataZodSchema } from '@/shared/utils/metadata';

export interface MetadataFormPreviewHeader {
  title: string;
  description?: string;
}

interface MetadataFormPreviewProps {
  schema?: JsonSchemaType;
  className?: string;
  header?: MetadataFormPreviewHeader | false;
}

type PreviewSection = { id: string; label: string; fieldNames: string[] };

type UseScrollSpyOptions = {
  root?: HTMLElement | null;
  rootMargin?: string;
  threshold?: number | number[];
};

function useScrollSpy(sectionIds: string[], options?: UseScrollSpyOptions) {
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);

  const stableSectionIds = useMemo(
    () => sectionIds.filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(sectionIds)]
  );

  useEffect(() => {
    if (stableSectionIds.length === 0) return;

    const elements = stableSectionIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (a.boundingClientRect.top ?? 0) - (b.boundingClientRect.top ?? 0));

        const firstVisible = visible[0]?.target as HTMLElement | undefined;
        if (firstVisible?.id) {
          setActiveSectionId(firstVisible.id);
        }
      },
      {
        root: options?.root ?? null,
        rootMargin: options?.rootMargin ?? '-20% 0px -70% 0px',
        threshold: options?.threshold ?? [0, 0.1, 0.25],
      }
    );

    for (const el of elements) observer.observe(el);
    return () => observer.disconnect();
  }, [stableSectionIds, options?.root, options?.rootMargin, options?.threshold]);

  return activeSectionId;
}

function getOrderedFieldNames(metadataSchema: JsonSchemaType) {
  const fromOrder = metadataSchema.propertyOrder?.length ? metadataSchema.propertyOrder : [];
  return fromOrder.length > 0 ? fromOrder : Object.keys(metadataSchema.properties || {});
}

function getKnownGroupIds(metadataSchema: JsonSchemaType) {
  return new Set((metadataSchema.groups ?? []).map((g) => g.id));
}

function getFieldsForGroup(metadataSchema: JsonSchemaType, groupId: string) {
  const ordered = getOrderedFieldNames(metadataSchema);
  return ordered.filter((name) => metadataSchema.properties?.[name]?.groupId === groupId);
}

function getUngroupedFields(metadataSchema: JsonSchemaType) {
  const ordered = getOrderedFieldNames(metadataSchema);
  const knownGroupIds = getKnownGroupIds(metadataSchema);
  return ordered.filter((name) => {
    const field = metadataSchema.properties?.[name];
    const fieldGroupId = field?.groupId;
    if (!fieldGroupId) return true;
    return !knownGroupIds.has(fieldGroupId);
  });
}

function pickZodSchema(schema: any, fieldNames: string[]) {
  const shape = (schema as unknown as { shape: Record<string, unknown> }).shape || {};
  const pick: Record<string, true> = {};
  for (const name of fieldNames) {
    if (Object.hasOwn(shape, name)) pick[name] = true;
  }
  return schema.pick(pick);
}

function PreviewSidemenu({
  sections,
  activeId,
  onSelect,
}: {
  sections: PreviewSection[];
  activeId: string | null;
  onSelect: (id: string) => void;
}) {
  const renderItem = (section: PreviewSection) => {
    const isActive = activeId === section.id;
    return (
      <Button
        key={section.id}
        type="button"
        variant="ghost"
        className={cn(
          'w-full justify-between px-3 py-2 h-auto text-sm font-medium rounded-lg',
          isActive
            ? 'bg-muted text-foreground hover:bg-muted'
            : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
        )}
        onClick={() => onSelect(section.id)}
      >
        <span className="truncate">{section.label}</span>
        <ChevronRight className={cn('size-4 shrink-0', isActive ? 'opacity-100' : 'opacity-40')} />
      </Button>
    );
  };

  return (
    <nav className="space-y-1" data-testid="metadata-form-preview-sidemenu">
      {sections.map(renderItem)}
    </nav>
  );
}

function PreviewForm({ schema }: { schema: JsonSchemaType }) {
  const { t, locale } = useIntl();
  const zodSchema = useMemo(() => createMetadataZodSchema(schema, 'gmn-admin'), [schema]);
  const form = useForm({
    resolver: zodResolver(zodSchema),
    defaultValues: {},
    mode: 'onChange',
  });

  const sections = useMemo<PreviewSection[]>(() => {
    const groups = [...(schema.groups ?? [])].sort((a, b) => a.order - b.order);
    const previewSections = groups
      .map((group) => ({
        id: `metadata-preview-group-${group.id}`,
        label: locale === 'en-US' && group.labelEn ? group.labelEn : group.labelDe,
        fieldNames: getFieldsForGroup(schema, group.id),
      }))
      .filter((s) => s.fieldNames.length > 0);

    const ungrouped = getUngroupedFields(schema);
    if (ungrouped.length > 0) {
      previewSections.push({
        id: 'metadata-preview-ungrouped',
        label: t('metadata.builder.ungrouped'),
        fieldNames: ungrouped,
      });
    }

    return previewSections;
  }, [locale, schema, t]);

  const contentRef = useRef<HTMLDivElement | null>(null);

  const activeSectionId = useScrollSpy(
    sections.map((s) => s.id),
    {
      root: contentRef.current,
      rootMargin: '-15% 0px -75% 0px',
    }
  );

  const scrollToSection = (id: string) => {
    scrollToTarget(id, { root: contentRef.current });
  };

  return (
    <Form {...form}>
      <form className="h-full">
        <div className="grid h-full lg:grid-cols-12">
          <div className="lg:col-span-3 border-r bg-muted/15 p-4">
            <div className="lg:hidden">
              <Select value={activeSectionId ?? sections[0]?.id} onValueChange={scrollToSection}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sections.map((section) => (
                    <SelectItem key={section.id} value={section.id}>
                      {section.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="hidden lg:block">
              <PreviewSidemenu
                sections={sections}
                activeId={activeSectionId ?? sections[0]?.id ?? null}
                onSelect={scrollToSection}
              />
            </div>
          </div>

          <div className="lg:col-span-9 overflow-auto" ref={contentRef}>
            <div className="p-6">
              <div className="space-y-10" data-testid="metadata-form-preview-content">
                {sections.map((section) => (
                  <div key={section.id} id={section.id} className="scroll-mt-6">
                    <div className="flex items-center gap-3 mb-4">
                      <h4 className="text-sm font-semibold text-foreground">{section.label}</h4>
                      <div className="h-px flex-1 bg-border" />
                    </div>

                    <MetadataAutoForm
                      schema={pickZodSchema(zodSchema, section.fieldNames)}
                      form={form}
                      metadataSchema={schema}
                      groupKey="gmn-admin"
                      disableGrouping={true}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
}

export function MetadataFormPreview({ schema, className, header }: MetadataFormPreviewProps) {
  const { t } = useIntl();

  const schemaKey = useMemo(() => JSON.stringify(schema ?? {}), [schema]);
  const hasFields = Boolean(schema && Object.keys(schema.properties ?? {}).length > 0);

  const showHeader = header !== false;
  const headerTitle = header ? header.title : t('metadata.builder.preview');
  const headerDescription = header ? header.description : t('metadata.builder.previewDescription');

  return (
    <div
      className={cn(
        'flex flex-col h-full bg-card rounded-lg border shadow-sm overflow-hidden',
        className
      )}
      data-testid="metadata-form-preview"
    >
      {showHeader && (
        <div className="border-b p-5">
          <h2 className="text-base font-semibold">{headerTitle}</h2>
          {headerDescription && (
            <p className="text-sm text-muted-foreground">{headerDescription}</p>
          )}
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-hidden">
        {!hasFields ? (
          <div className="h-full flex items-center justify-center text-muted-foreground p-6">
            {t('metadata.builder.noFieldsToPreview')}
          </div>
        ) : (
          <PreviewForm key={schemaKey} schema={schema as JsonSchemaType} />
        )}
      </div>
    </div>
  );
}
